import { Badge } from "@designs-by-maryam/ui/components/badge";
import { Button } from "@designs-by-maryam/ui/components/button";
import { cn } from "@designs-by-maryam/ui/lib/utils";
import { useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import {
	ArrowRight,
	Award,
	CheckCircle,
	Heart,
	type LucideIcon,
	Sparkles,
	Star,
	Trophy,
	Users,
} from "lucide-react";
import { motion } from "motion/react";
import { useMemo } from "react";
import { useSafeMotion } from "@/lib/motion";
import { siteSettingsQueryOptions } from "@/lib/query-options";

type AboutLandingProps = {
	className?: string;
};

// CMS About Settings type (mirrors @designs-by-maryam/db/schema/site-settings)
type CMSAboutSettings = {
	hero?: { bio?: string };
	stats?: Array<{ value: string; label: string }>;
	differentiators?: Array<{ icon: string; title: string; description: string }>;
	expertise?: string[];
};

// Icon name to Lucide component mapping
const ICON_MAP: Record<string, LucideIcon> = {
	trophy: Trophy,
	users: Users,
	checkCircle: CheckCircle,
	star: Star,
	award: Award,
	heart: Heart,
	sparkles: Sparkles,
};

// Static fallback data (used when CMS unavailable)
const STATIC_FALLBACK = {
	stats: [
		{ value: "5+", label: "Years Experience" },
		{ value: "50+", label: "Projects Completed" },
	],
	differentiators: [
		{
			icon: "trophy",
			title: "Top 10 Graduate",
			description: "University of Bahrain Interior Design",
		},
		{
			icon: "users",
			title: "Cultural Sensitivity",
			description: "Blending modern design with traditional values",
		},
		{
			icon: "checkCircle",
			title: "Comprehensive Service",
			description: "From concept to completion",
		},
	],
	expertise: [
		"3D Visualization",
		"AutoCAD",
		"Space Planning",
		"Project Management",
	],
	bio: `My design journey began with a simple belief: every space has
the potential to transform lives. As a top graduate from the
University of Bahrain, I combine contemporary design principles
with cultural sensitivity to create spaces that truly reflect
their inhabitants.`,
};

/**
 * AboutLanding Component
 *
 * A concise "About Me" section for the homepage that provides
 * a quick introduction and links to the full About page.
 *
 * Features:
 * - Brief biography with Edison quote
 * - Key stats (years experience, projects completed)
 * - Differentiators and expertise badges
 * - CTA buttons to About page and Contact
 * - CMS-integrated with static fallback
 */
export function AboutLanding({ className }: AboutLandingProps) {
	const { slideUp, fadeIn, viewportOnce, shouldReduce } = useSafeMotion();

	// Fetch about settings from CMS with static fallback
	const { data, isError } = useQuery({
		...siteSettingsQueryOptions("about"),
		retry: 1,
		staleTime: 60 * 60 * 1000, // 1 hour
	});

	// Normalize CMS data with fallback
	const aboutData = useMemo(() => {
		const cmsAbout = data?.value as CMSAboutSettings | undefined;

		// Use static fallback if error or missing data
		if (isError || !cmsAbout) {
			return STATIC_FALLBACK;
		}

		return {
			stats: cmsAbout.stats?.length ? cmsAbout.stats : STATIC_FALLBACK.stats,
			differentiators: cmsAbout.differentiators?.length
				? cmsAbout.differentiators
				: STATIC_FALLBACK.differentiators,
			expertise: cmsAbout.expertise?.length
				? cmsAbout.expertise
				: STATIC_FALLBACK.expertise,
			bio: cmsAbout.hero?.bio ?? STATIC_FALLBACK.bio,
		};
	}, [data, isError]);

	// Resolve icon names to components
	const highlights = useMemo(
		() =>
			aboutData.differentiators.map((d) => ({
				icon: ICON_MAP[d.icon] ?? CheckCircle,
				title: d.title,
				description: d.description,
			})),
		[aboutData.differentiators],
	);

	return (
		<section
			aria-label="About Maryam"
			className={cn("py-16 md:py-24", className)}
		>
			<div className="container mx-auto px-4 md:px-6">
				<div className="grid items-start gap-12 lg:grid-cols-2 lg:gap-16">
					{/* Left Column - Content */}
					<motion.div
						className="space-y-8"
						initial={slideUp.initial}
						transition={slideUp.transition}
						viewport={viewportOnce}
						whileInView={slideUp.animate}
					>
						{/* Heading */}
						<div className="space-y-4 text-center lg:text-left">
							<h2 className="font-bold text-3xl tracking-tight md:text-4xl lg:text-5xl">
								About Me
							</h2>
							<p className="text-lg text-muted-foreground">
								Interior Designer & Creative Visionary
							</p>
						</div>

						{/* Story */}
						<div className="space-y-6">
							<p className="text-muted-foreground leading-relaxed">
								{aboutData.bio}
							</p>

							{/* Quote */}
							<div className="rounded-2xl border border-primary/20 bg-primary/5 p-6 dark:border-primary/30 dark:bg-primary/10">
								<blockquote className="mb-3 text-lg text-muted-foreground italic">
									"If we did all the things we are capable of we would literally
									astound ourselves."
								</blockquote>
								<cite className="font-medium text-primary text-sm">
									— Thomas Edison
								</cite>
							</div>
						</div>

						{/* CTA */}
						<div className="flex flex-col gap-4 sm:flex-row">
							<Button asChild size="lg" variant="brand">
								<Link to="/about">
									Learn More About Me
									<ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
								</Link>
							</Button>
							<Button asChild size="lg" variant="brand-outline">
								<Link to="/contact">Get In Touch</Link>
							</Button>
						</div>
					</motion.div>

					{/* Right Column - Stats + Two Boxes */}
					<motion.div
						className="space-y-6"
						initial={fadeIn.initial}
						transition={{
							...fadeIn.transition,
							delay: shouldReduce ? 0 : 0.2,
						}}
						viewport={viewportOnce}
						whileInView={fadeIn.animate}
					>
						{/* Stats Cards */}
						<div className="grid grid-cols-2 gap-4">
							{aboutData.stats.slice(0, 2).map((stat) => (
								<div
									className="rounded-2xl border bg-card p-6 text-center shadow-sm transition-shadow hover:shadow-md"
									key={stat.label}
								>
									<div className="mb-2 font-bold text-3xl text-primary tabular-nums">
										{stat.value}
									</div>
									<div className="text-muted-foreground text-sm">
										{stat.label}
									</div>
								</div>
							))}
						</div>

						{/* What Makes Me Different */}
						<div className="rounded-2xl border border-primary/20 bg-background/50 p-6 backdrop-blur-sm dark:border-primary/30">
							<h4 className="mb-4 font-semibold text-base tracking-tight">
								What Makes Me Different
							</h4>
							<div className="space-y-3">
								{highlights.map((highlight) => (
									<div className="flex items-start gap-3" key={highlight.title}>
										<highlight.icon className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
										<div className="space-y-0.5">
											<p className="font-medium text-foreground text-sm">
												{highlight.title}
											</p>
											<p className="text-muted-foreground text-xs">
												{highlight.description}
											</p>
										</div>
									</div>
								))}
							</div>
						</div>

						{/* Core Expertise */}
						<div className="rounded-2xl border border-primary/20 bg-background/50 p-6 backdrop-blur-sm dark:border-primary/30">
							<h4 className="mb-4 font-semibold text-base tracking-tight">
								Core Expertise
							</h4>
							<div className="flex flex-wrap gap-2">
								{aboutData.expertise.map((skill) => (
									<Badge
										className="rounded-lg bg-primary/10 px-3 py-1.5 font-medium text-primary text-xs transition-colors hover:bg-primary/20 dark:bg-primary/20 dark:text-primary dark:hover:bg-primary/30"
										key={skill}
										variant="secondary"
									>
										{skill}
									</Badge>
								))}
							</div>
						</div>
					</motion.div>
				</div>
			</div>
		</section>
	);
}

export default AboutLanding;
